import { StateCreator } from 'zustand';

import {
  LmmMaterieelTimed,
  LmmPeilmoment,
  LmmResponse,
  LmmTijdstip,
  LmmTijdstipLabel,
  LmmVerrijktV2,
  LmmVoorraadResponse,
} from '@/apps/lmm/types/LmmVoorraad';

import { getLmmMaterieelCategoryFull } from '../utils/lmmMaterieelCategory';

import { LmmVoorraadStore } from './types/LmmVoorraadStore';
import { defaultLmmMaterieelTimed } from './lmmStateUtils';

/** V2 implementatie van sortMaterieelOrder */
const sortMaterieelOrderV2 = (
  lmmMaterieelTimed: Map<string, LmmMaterieelTimed>,
  openTijdstip: LmmTijdstipLabel | null,
  field: 'materieel' | 'overOfTekort',
  direction: 'asc' | 'desc',
): Map<string, LmmMaterieelTimed> => {
  const matEntries = Array.from(lmmMaterieelTimed.entries());
  const openTijdKey = openTijdstip?.toUpperCase() as LmmPeilmoment;
  // sort mat entries
  matEntries.sort((entryA, entryB) => {
    if (field === 'materieel')
      return direction === 'asc' ? entryA[0].localeCompare(entryB[0]) : entryB[0].localeCompare(entryA[0]);
    if (field === 'overOfTekort' && openTijdstip) {
      console.log({ openTijdKey, a: entryA[1], b: entryB[1] });
      const overA = entryA[1]?.[openTijdKey]?.overOfTekort ?? 0;
      const overB = entryB[1]?.[openTijdKey]?.overOfTekort ?? 0;
      return direction === 'asc' ? overA - overB : overB - overA;
    }
    return 0;
  });
  return new Map<string, LmmMaterieelTimed>(matEntries);
};

const lmmVoorraadStateCreator: StateCreator<LmmVoorraadStore> = (set, get, __) => ({
  lmmTijdstippen: [],
  setLmmTijdstippen(tijdstippen) {
    set({ lmmTijdstippen: tijdstippen });
  },

  lmmOpenTijdstip: null,
  lmmOpenBenodigdOpbouw: false,
  setLmmOpenTijdstip(label) {
    set((state) => ({
      lmmOpenTijdstip: state.lmmOpenTijdstip === label ? null : label,
      lmmOpenBenodigdOpbouw: false,
      lmmMaterieelOrder: sortMaterieelOrderV2(
        state.lmmMaterieelTimed,
        state.lmmOpenTijdstip === label ? null : label,
        state.lmmSortField,
        state.lmmSortDirection,
      ),
    }));
  },
  toggleLmmOpenBenodigdOpbouw() {
    set((state) => ({ lmmOpenBenodigdOpbouw: !state.lmmOpenBenodigdOpbouw }));
  },

  lmmSortField: 'materieel',
  lmmSortDirection: 'asc',
  setLmmSort(field) {
    console.log({ field });
    set((state) => {
      const lmmSortDirection = state.lmmSortField === field && state.lmmSortDirection === 'asc' ? 'desc' : 'asc';
      return {
        lmmSortField: field,
        lmmSortDirection,
        lmmMaterieelTimed: sortMaterieelOrderV2(
          state.lmmMaterieelTimed,
          state.lmmOpenTijdstip,
          field,
          lmmSortDirection,
        ),
      };
    });
  },

  resetLmmVoorraad() {
    set({
      lmmTijdstippen: [],
      lmmOpenTijdstip: null,
      lmmOpenBenodigdOpbouw: false,
      lmmSortField: 'materieel',
      lmmSortDirection: 'asc',
      lmmMaterieelTimed: new Map(),
    });
  },

  // v2
  lmmMaterieelTimed: new Map<string, LmmMaterieelTimed>(),
  async setLmmResponse(lmmVoorraadResponse: LmmResponse) {
    const getVoorraadVerrijktV2 = (voorraad: LmmVoorraadResponse): LmmVerrijktV2 => {
      const { dienst, gdst, ebksb } = voorraad.benodigd;
      const overOfTekort = voorraad.brutoPark - (dienst + gdst + ebksb + voorraad.nietInzetbaar.aantalEenheden);
      const matCategory = getLmmMaterieelCategoryFull(voorraad.materieel);
      return {
        ...voorraad,
        overOfTekort,
        matCategory,
        nvt: false,
      };
    };

    const mapVoorraad = (): Map<string, LmmMaterieelTimed> => {
      return lmmVoorraadResponse.reduce((obj, timed) => {
        for (const voorraad of timed.voorraad) {
          const materieelTimed =
            obj.get(voorraad.materieel) ?? defaultLmmMaterieelTimed({ materieel: voorraad.materieel });
          materieelTimed[timed.peilmoment] = getVoorraadVerrijktV2(voorraad);
          obj.set(voorraad.materieel, materieelTimed);
        }
        return obj;
      }, new Map<string, LmmMaterieelTimed>());
    };

    set((state) => {
      const lmmMaterieelTimed = sortMaterieelOrderV2(
        mapVoorraad(),
        state.lmmOpenTijdstip,
        state.lmmSortField,
        state.lmmSortDirection,
      );

      const lmmTijdstippen = lmmVoorraadResponse.map<LmmTijdstip>((moment) => {
        const label = moment.peilmoment === 'NU' ? 'Nu' : moment.peilmoment;
        return {
          label,
          dateTime: new Date(moment.peildatumtijd),
        };
      });
      return { lmmTijdstippen, lmmMaterieelTimed };
    });
  },
});
export default lmmVoorraadStateCreator;
